import { useState, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import type { Participant } from '../../types';

interface GroupFormProps {
  availableParticipants: Participant[];
  onSubmit: (name: string, participantIds: string[], representativeId: string) => void;
  onCancel: () => void;
}

export function GroupForm({ availableParticipants, onSubmit, onCancel }: GroupFormProps) {
  const [name, setName] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [representativeId, setRepresentativeId] = useState('');

  const handleToggleParticipant = (id: string) => {
    setSelectedIds((prev) => {
      const newIds = prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id];
      // If representative is deselected, clear it
      if (!newIds.includes(representativeId)) {
        setRepresentativeId(newIds[0] || '');
      }
      return newIds;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedIds.length > 0 && representativeId) {
      let finalName = name.trim();

      if (!finalName) {
        // Generate automatic name based on selected participants
        const selectedParticipants = availableParticipants.filter((p) =>
          selectedIds.includes(p.id)
        );
        finalName = selectedParticipants.map((p) => p.name).join('+');
      }

      onSubmit(finalName, selectedIds, representativeId);
    }
  };

  const isValid = selectedIds.length > 0 && representativeId;

  const validationMessage = useMemo(() => {
    if (selectedIds.length === 0) return 'Selecciona al menos un participante';
    if (!representativeId) return 'Selecciona un representante del grupo';
    return null;
  }, [selectedIds, representativeId]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="group-name">Nombre del grupo</Label>
        <Input
          id="group-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Opcional - Ej: Pareja 1"
          autoFocus
        />
      </div>

      <div className="space-y-2">
        <Label>Miembros</Label>
        {availableParticipants.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No hay participantes disponibles. Agrega participantes primero.
          </p>
        ) : (
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {availableParticipants.map((participant) => (
              <label
                key={participant.id}
                className="flex items-center gap-3 p-2 rounded-md hover:bg-accent/50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.includes(participant.id)}
                  onChange={() => handleToggleParticipant(participant.id)}
                  className="h-4 w-4 rounded border-input"
                />
                <span className="text-sm flex-1">{participant.name}</span>
                {selectedIds.includes(participant.id) && (
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <input
                      type="radio"
                      name="representative"
                      checked={representativeId === participant.id}
                      onChange={() => setRepresentativeId(participant.id)}
                      className="h-3 w-3"
                    />
                    Representante
                  </label>
                )}
              </label>
            ))}
          </div>
        )}
      </div>

      {validationMessage && (
        <p className="text-sm text-muted-foreground">{validationMessage}</p>
      )}

      <div className="flex gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1">
          Cancelar
        </Button>
        <Button type="submit" disabled={!isValid} className="flex-1">
          Crear grupo
        </Button>
      </div>
    </form>
  );
}
